/**
 * Sprint 233 — Client's own list of package orders.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Loader2, Sparkles, Plus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABEL = {
  new_order: 'New Order',
  awaiting_quote: 'Awaiting Quote',
  awaiting_payment: 'Awaiting Payment',
  awaiting_creator_assignment: 'Matching Creator',
  creator_assigned: 'Creator Assigned',
  in_progress: 'In Progress',
  content_submitted: 'Content Submitted',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const ClientPackageOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/client/package-orders');
        if (!cancelled) setOrders(res.data || []);
      } catch {
        if (!cancelled) toast.error('Could not load orders');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F14] text-white" data-testid="client-package-orders">
      <header className="sticky top-0 z-30 border-b border-white/5 bg-[#0B0F14]/95 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/client/packages')}
                  className="text-white/70 hover:text-white hover:bg-white/5">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Sparkles className="w-4 h-4 text-[#0DEAE3]" />
          <h1 className="text-base font-semibold">My Package Orders</h1>
          <div className="flex-1" />
          <Button onClick={() => navigate('/client/packages')}
                  className="bg-[#0DEAE3] text-[#0B0F14] hover:bg-[#0DEAE3]/90" size="sm">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Order
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6">
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0DEAE3]" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 text-white/40">
            <p>You haven&apos;t placed any package orders yet.</p>
            <Button onClick={() => navigate('/client/packages')}
                    className="mt-5 bg-[#0DEAE3] text-[#0B0F14] hover:bg-[#0DEAE3]/90 font-semibold">
              Browse Packages <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {orders.map(o => (
              <div key={o.id} className="rounded-xl bg-white/[0.04] border border-white/[0.08] p-4" data-testid={`my-order-${o.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-white">{o.package?.name || o.package_slug}</p>
                      <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-[#0DEAE3]/15 text-[#0DEAE3] border border-[#0DEAE3]/30">
                        {STATUS_LABEL[o.status] || o.status}
                      </span>
                    </div>
                    <p className="text-xs text-white/50 mt-0.5">
                      Placed {new Date(o.created_at).toLocaleString()}
                      {' · '}{o.mode === 'auto_match' ? 'Auto-Match' : 'Browse & Pick'}
                      {o.category && ` · ${o.category}`}
                    </p>
                    {(o.assignments || []).length > 0 && (
                      <p className="text-xs text-emerald-400 mt-1">
                        {o.assignments.length} creator{o.assignments.length !== 1 ? 's' : ''} assigned
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-white">
                      {o.amount_cents == null
                        ? (o.quote_amount_cents ? `$${(o.quote_amount_cents / 100).toFixed(0)}` : 'Quote')
                        : `$${(o.amount_cents / 100).toFixed(0)}`}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-white/40">{o.payment_status}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ClientPackageOrders;
